import { FunctionComponent, useState } from 'react';
import CalendarBooking from './CalendarBooking';
import DateSelectorComponent from './DateSelectorComponent';
import moment, { Moment } from 'moment-timezone';
import HoursBooking from './HoursBooking';
import { useMarketplace } from 'hooks';
import styles from './DateSelectorSection.module.scss';
import clsx from 'clsx';
import ConfirmClosePopup from 'components/CreateBooking/UI/ConfirmClosePopup';

interface Props {
    date: Moment;
    slotTimes?: Array<string>;
    withWarning?: boolean;
    disabled?: boolean;
    multiplePet?: boolean;
    onSelectDate: (date: Moment) => void;
}

const getDateFormatted = (date: Moment) => {
    const minutes = date.minutes();
    const dateFormatted = date;

    if (minutes > 0 && minutes <= 15) {
        dateFormatted.add(15 - minutes, 'minutes');
    } else if (minutes > 15 && minutes <= 30) {
        dateFormatted.add(30 - minutes, 'minutes');
    } else if (minutes > 30 && minutes <= 45) {
        dateFormatted.add(45 - minutes, 'minutes');
    } else if (minutes > 45 && minutes <= 59) {
        dateFormatted.add(60 - minutes, 'minutes');
    }

    return dateFormatted;
};

export const DateSelectorSection: FunctionComponent<Props> = props => {
    const marketplace = useMarketplace();

    const [calendarExpanded, setCalendarExpanded] = useState(false);
    const [hoursExpanded, setHoursExpanded] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [dateSelected, setDateSelected] = useState<Moment | undefined>(undefined);

    const handleExpandCalendar = () => {
        setCalendarExpanded(!calendarExpanded);
        setHoursExpanded(false);
    };

    const handleExpandHours = () => {
        setCalendarExpanded(false);
        setHoursExpanded(!hoursExpanded);
    };

    const handleSelectDate = (dateSelected: Date) => {
        setCalendarExpanded(false);
        if (props.multiplePet && props.withWarning) {
            setShowWarning(true);
            setDateSelected(
                moment.tz(
                    `${moment(dateSelected).format('YYYY-MM-DD')} ${props.date.format('LT').toUpperCase()}`,
                    'YYYY-MM-DD hh:mm A',
                    marketplace.schedule.timeZone
                )
            );
        } else {
            props.onSelectDate(
                moment.tz(
                    `${moment(dateSelected).format('YYYY-MM-DD')} ${props.date.format('LT').toUpperCase()}`,
                    'YYYY-MM-DD hh:mm A',
                    marketplace.schedule.timeZone
                )
            );
        }
    };

    const handleSelectHours = (hourValue: string) => {
        const dateHour = moment.tz(
            `${getDateFormatted(props.date).format('YYYY-MM-DD')} ${hourValue.toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        setHoursExpanded(false);
        props.onSelectDate(dateHour);
    };

    const confirmChangeDateDialog = (
        <ConfirmClosePopup
            title=""
            labelYes="Yes"
            question="You are about to change the date of all services in this booking. Do you want to continue?"
            onDiscard={() => {
                setShowWarning(false);
                setDateSelected(undefined);
            }}
            onSave={() => {
                props.onSelectDate(dateSelected!);
                setShowWarning(false);
                setDateSelected(undefined);
            }}
        />
    );

    return (
        <div className={styles.container}>
            <div>
                <p className={styles.title}>Date</p>
                <div className={styles.sectionCalendar}>
                    <span className={styles.text}>{props.date.format('ddd, DD MMM YYYY')}</span>
                    <DateSelectorComponent
                        id="date"
                        expanded={calendarExpanded}
                        className={styles.contentDate}
                        onExpand={handleExpandCalendar}
                        onClickAway={() => setCalendarExpanded(false)}
                    >
                        <CalendarBooking
                            date={getDateFormatted(props.date)}
                            onDaySelect={handleSelectDate}
                        />
                    </DateSelectorComponent>
                </div>
            </div>
            <div className={clsx({ [styles.disabled]: props.disabled })}>
                <p className={styles.title}>Start Time</p>
                <div className={styles.section}>
                    <span className={styles.text}>{props.date.format('LT').toLowerCase()}</span>
                    <DateSelectorComponent
                        id="hour"
                        expanded={hoursExpanded}
                        className={styles.contentHours}
                        onExpand={handleExpandHours}
                        onClickAway={() => setHoursExpanded(false)}
                    >
                        <HoursBooking
                            dayMoment={getDateFormatted(props.date)}
                            slotTimes={props.slotTimes}
                            onSelectHour={handleSelectHours}
                        />
                    </DateSelectorComponent>
                </div>
            </div>
            {showWarning && confirmChangeDateDialog}
        </div>
    );
};

export default DateSelectorSection;
